/**
 * useStudentLive — le live de l'élève, branché : la salle commune (`useLiveRoom`), la machine de
 * l'élève (`studentLiveMachine`), l'appel (`useStudentLiveConnection`), l'attente du tuteur
 * (`useTutorWaiting`), le chat (`useLiveChat`), la sortie et la note (`useLiveLeave`).
 *
 * Il traduit en événements de la machine :
 *  - le canal de la salle : `finalizeLive`, `newTodo`, `deleteTodo`, `tutorFinalizedActivity`,
 *    `newMessage`, `checkpointTriggerResponse` (les mêmes noms, les mêmes champs ; seuls ceux adressés à
 *    l'élève, d'un autre que lui) ;
 *  - les attributs de SON participant : `sound_mode`, `hand_raised` ;
 *  - les réponses du serveur : `joinCall`, `getCall` à chaque reprise, `raiseHand` ;
 *  - le relevé : la séance du jour close ou rouverte.
 *
 * Après une reprise, il relit ce que la coupure a pu faire perdre : le son et la main (`getCall`), la
 * liste des activités (`getUser` : une création, un ordre, un retrait). Un « Afficher » du tuteur
 * perdu pendant la coupure n'a pas de source serveur : il ne se rattrape pas (reste écrit dans
 * l'issue du chantier).
 *
 * Il ne joue rien lui-même : un son, une navigation, la fin, il les remonte à l'écran (`onCue`,
 * `onEnded`, `onReopened`). Ce que la fin fait d'une sortie de l'élève qu'elle croise, c'est la machine
 * qui le dit (`LEAVE_COMMITTED`, `LEAVE_OUT`).
 */
import { useLocalParticipant } from "@livekit/components-react"
import { type LocalAudioTrack } from "livekit-client"
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react"
import { AppState } from "react-native"
import { useDispatch, useSelector } from "react-redux"

import { pauseActivity } from "../../../data/redux/actions/activity"
import { apiRequest } from "../../../data/redux/actions/api"
import { updateLastConnected } from "../../../data/redux/actions/authTokens"
import { clearCheckpointConversation, setCheckpointConversation } from "../../../data/redux/actions/liveMode"
import { authService } from "../../../data/services/authService"
import { liveService } from "../../../data/services/liveService"
import { liveServiceV2 } from "../../../data/services/liveServiceV2"
import { supportsNoShowRecovery } from "../../../data/utility/liveSessionType"
import { ownNetworkFailures } from "../../../data/utility/networkFailureOwner"
import { hasFeature } from "../../../data/utility/Utils"
import { useSignalReducer } from "../../shared/useSignalReducer"
import { apiCall, ENGINE_REQUEST_OPTIONS } from "../../shared/apiCall"
import { createTelemetry, type EngineTelemetry } from "../../shared/telemetry"
import { useInterval } from "../../shared/useInterval"
import { type LiveDataEvent } from "../room/dataChannel"
import { snapshotOf, type RoomExit } from "../room/roomMachine"
import useThrottledSetAttributes, { setThrottledAttributes } from "../room/useThrottledSetAttributes"
import { useDisconnectFromLive } from "../room/useDisconnectFromLive"
import { livePeople, myOtherDevices, type LivePerson } from "../room/livePeople"
import { useLiveRoom, type LiveRoom } from "../room/useLiveRoom"
import {
    checkpointCreationBody,
    checkpointOpeningReducer,
    creationAbandoned,
    initialCheckpointOpening,
    initialSamHold,
    liveCheckpointToShow,
    samHoldReducer,
    samSentOutcome,
    type CheckpointOpening,
    type CheckpointOpeningEvent,
    type CheckpointOpeningState,
    type SamHold,
    type SamHoldEvent,
    type LiveCheckpointConversation,
    type LiveCheckpointTrigger,
} from "./liveCheckpoint"
import { liveExitNotice, type LiveExitNoticeKind } from "./liveExitNotice"
import { liveTutorCast, type LiveTutorCast } from "./liveTutorCast"
import { markStudentLiveScreen } from "./liveScreenPresence"
import { accountHasActivity, readNewTodo } from "./newTodoEvent"
import {
    callReplyFor,
    initialStudentLiveState,
    PUSHED_ON_SCREEN_WITHIN_MS,
    pushedActivityOutOfSight,
    studentLiveReducer,
    type StudentLiveCue,
    type StudentLiveSignal,
    type StudentLiveState,
    type StudentLiveView,
} from "./studentLiveMachine"
import {
    chatChannelToOpen,
    resolveChatChannel,
    useLiveChat,
    type LiveChatChannel,
    type LiveChatController,
    type LiveChatVoiceOutbox,
} from "./useLiveChat"
import { useLiveLeave, type LiveExit, type LiveLeaveController } from "./useLiveLeave"
import { useLiveNotificationTap } from "./useLiveNotificationTap"
import { type MicGateView } from "./micGate"
import { type MicBlocker, type MicRefusalView } from "./micRefusal"
import { useCameraRefusal } from "./useCameraRefusal"
import { useMicGate } from "./useMicGate"
import { useMicRefusal } from "./useMicRefusal"
import { useMicDrop } from "./useMicDrop"
import { useMicSilence } from "./useMicSilence"
import { useStudentLiveConnection } from "./useStudentLiveConnection"
import { useTutorWaiting, type TutorWaitingController } from "./useTutorWaiting"

/** Un son que l'écran joue : ceux de la machine, et ceux des messages du live. */
export type StudentLiveSound = StudentLiveCue | "new_message" | "activity_validated" | "attention"

/** Ce qui interrompt le live : la séance disparue, le paiement en attente, pas le jour du live, ou un autre onglet. */
export type StudentLiveNotice = LiveExitNoticeKind | "several_tabs"

export {
    LIVE_CHECKPOINT_CONTEXTS,
    STUDENT_TAP_TRIGGER,
    type CheckpointOpening,
    type LiveCheckpointConversation,
    type LiveCheckpointTrigger,
} from "./liveCheckpoint"

/** Le délai d'aujourd'hui avant l'écran de fin, après le `finalizeLive` (`useLiveKitEventListener`). */
export const END_NAVIGATION_DELAY_MS = 2000
/** `updateLastConnected`, comme `LiveContainerV3`. */
export const LAST_CONNECTED_EVERY_MS = 5 * 60 * 1000
/**
 * Le plafond d'une reprise du SDK, réseau présent, au-delà duquel la salle est relancée et « Réessayer »
 * paraît (`useLiveRoom`). Le SDK, lui, peut rester en reprise sans plus rien tenter (sa signalisation
 * rouverte puis refermée avant son premier message). Vingt secondes : sa politique (`roomOptions`) a
 * tenté cinq fois avant 9 s, puis toutes les 7 à 8 s ; et il retente dès que le navigateur revient en
 * ligne. Une reprise qui aboutit le fait en quelques secondes : vingt secondes de réseau sans issue, ce
 * n'en est plus une. Le temps hors ligne ne compte pas. Le natif a le même SDK et le même blocage (une
 * reprise tenue 2 min 44 après 26 s de coupure sur Android) : la même règle y vaut. Sa relance ne double
 * pas le pilote natif (`useStudentLiveConnection`), qui ne connecte qu'une salle restée à l'arrêt.
 */
export const STUDENT_RECONNECT_CEILING_MS = 20 * 1000

export type ApiStudentLiveSession = {
    id?: number
    tutor?: { id?: number; display_name?: string | null } | null
    users?: { id?: number; is_tutor?: boolean; avatar?: string | null }[] | null
    live_room?: { type?: string; supports_no_show_recovery?: boolean; version_ft?: unknown } | null
    thread_id?: number | string | null
    thread_ids?: Record<string, number | string | null> | null
    unread_private_messages_count?: Record<string, number | null | undefined> | null
    unread_messages_count?: number | null
    next_occurrences?: { date?: string; finalized_at?: string | null; substitute_id?: number | string | null }[] | null
    delay_today?: number | null
    tutor_absence_dispatch_after_minutes?: number | null
} & Record<string, unknown>

export type ApiStudentLiveUser = {
    id?: number
    display_name?: string | null
    email?: string | null
    live_sessions?: ApiStudentLiveSession[] | null
    todo_activities?: { id?: number; isDeleted?: boolean }[] | null
    /** Les activités faites. */
    activities?: { id?: number }[] | null
} & Record<string, unknown>

export type UseStudentLiveOptions = {
    onCue?: (sound: StudentLiveSound) => void
    /**
     * La séance est close : l'écran mène au rapport. `left` : l'élève a engagé sa sortie, sa note
     * donnée ou déclinée, et il ne lui reste qu'à quitter le rapport. Redit avec `left` quand il la
     * touche sur un rapport déjà affiché.
     */
    onEnded?: (exit: { left: boolean }) => void
    /** La séance close a été rouverte : l'écran revient au live. */
    onReopened?: () => void
    /** L'élève sort du live : l'écran qui suit se pousse là, avant que le live s'éteigne. */
    onOut?: (exit: LiveExit) => void
    /** Le tuteur vient de pousser une activité : ce que l'écran a ouvert par-dessus (le quiz) s'interrompt. */
    onActivityPushed?: () => void
    /** Les tests passent la leur. */
    telemetry?: EngineTelemetry
    /** La file des vocaux du chat : celle de la messagerie, que l'écran donne. */
    voiceOutbox: LiveChatVoiceOutbox
    /** L'accès au micro lu refusé à l'ouverture du live (`useMediaPermissions`, que l'écran lit). */
    micPermissionDenied?: boolean
}

/** Un autre de mes appareils dans la salle : son identité LiveKit, et s'il filme. */
export type LiveOtherDevice = { identity: string; cameraOn: boolean }

export type StudentLive = {
    liveSessionId: number | null
    user: ApiStudentLiveUser | null
    liveSession: ApiStudentLiveSession | null
    room: LiveRoom
    state: StudentLiveState
    dialogue: boolean
    micOn: boolean
    /** Le micro refusé par le navigateur : la boîte qui le dit en Dialogue, le point du micro (`micRefusal`). */
    micRefusal: MicRefusalView
    /**
     * Le micro tenu jusqu'à un geste de l'élève (`micGate`) : `held`, la salle ne l'ouvre pas d'elle-même
     * (l'écran ne monte pas `MuteCurrentUser`) ; `ask`, « Active ton micro » en Dialogue.
     */
    micGate: MicGateView
    /** En Dialogue, le micro ouvert ne capte aucun son (`micSilence`) : coupé par l'ordinateur. Web seulement. */
    micSilent: boolean
    /** En Dialogue, le micro coupé sans geste de l'élève (`micDrop`) : la piste perdue. Web seulement. */
    micDropped: boolean
    cameraOn: boolean
    /** Une caméra refusée vue en séance (`useCameraRefusal`), même quand l'accès ne se lit pas. */
    cameraBlocker: MicBlocker | null
    poorConnection: boolean
    /**
     * Ce que la scène dit de la connexion : elle s'établit, elle se rétablit, ou la salle a refusé le
     * jeton redemandé (`unreachable`, plus de relance avant « Réessayer »), ou rien.
     */
    connection: "connecting" | "reconnecting" | "unreachable" | null
    notice: StudentLiveNotice | null
    /** Le message du serveur sous la notice d'une sortie (le paiement en attente, le jour du live). */
    noticeMessage: string | null
    /** « Activité soumise ! » quand le tuteur rend l'activité ouverte à la place de l'élève. */
    validatedNotice: string | null
    /** L'activité que le tuteur vient d'archiver alors qu'elle était ouverte (`deleteTodo`). */
    removedActivityId: number | null
    /** L'activité que le tuteur vient de rendre à la place de l'élève (`tutorFinalizedActivity`). */
    tutorFinalizedId: number | null
    /** L'activité poussée par le tuteur, telle que le canal l'a portée (avant la relecture de la liste). */
    pushedActivity: Record<string, unknown> | null
    /** La conversation de Sam à poser (main levée, silence), `null` sinon — jamais en Dialogue. */
    checkpoint: LiveCheckpointConversation | null
    checkpointsEnabled: boolean
    /** Sam touché : son panneau se pose avant sa première phrase (`liveCheckpoint`). */
    checkpointOpening: CheckpointOpening
    waiting: TutorWaitingController
    /** Le tuteur que l'élève a devant lui (le principal), les autres tuteurs connectés, leurs caméras. */
    tutors: LiveTutorCast
    /**
     * Mes autres appareils dans la salle (un navigateur resté ouvert, le téléphone), dans l'ordre de la
     * salle : la scène les montre (Wajih, daily du 21/10). Vide : je ne suis connecté qu'ici.
     */
    otherDevices: LiveOtherDevice[]
    chat: LiveChatController & {
        open: boolean
        /** Le fil ouvert : celui de la puce choisie, le tuteur de la séance sans choix (`chatChannels`). */
        channel: LiveChatChannel
        setOpen: (open: boolean) => void
        /** `null` : le fil du tuteur de la séance. */
        setChannel: (channel: LiveChatChannel | null) => void
    }
    leave: LiveLeaveController
    /** Les entrées que l'activité composée reçoit (`ActivityLiveInputs`). */
    activityPaused: boolean
    send: LiveRoom["send"]
    setAttributes: (attributes: Record<string, string>) => Promise<void>
    telemetry: EngineTelemetry
    actions: {
        toggleHand: () => void
        toggleMic: () => void
        /** « Réessayer » de la boîte du micro refusé : à appeler dans le geste. */
        retryMic: () => void
        dismissMicRefusal: () => void
        /** « Activer mon micro » : à appeler dans le geste. */
        activateMic: () => void
        dismissMicAsk: () => void
        toggleCamera: () => void
        changeView: (view: StudentLiveView) => void
        /** La page dit sa forme : `tabs`, une vue à la fois (le téléphone). */
        changeLayout: (tabs: boolean) => void
        dismissTutorTalking: () => void
        dismissMicHint: () => void
        dismissSamBubble: () => void
        dismissNewActivity: () => void
        /** « Nouvelle activité de ton masteur » : son délai passé, ou l'activité retirée. */
        dismissAddedActivity: () => void
        dismissSamSentNotice: () => void
        dismissValidated: () => void
        /** `chosen` : le geste de l'élève — au téléphone, la vue passe sur l'activité. */
        selectActivity: (activityId: number | null, chosen?: boolean) => void
        /** La page dit si l'activité se voit. */
        reportActivityOnScreen: (onScreen: boolean) => void
        /**
         * Une conversation de Sam, sous ce qui l'ouvre (`trigger`) : Sam touché par l'élève
         * (`STUDENT_TAP_TRIGGER`), ou envoyé par le tuteur (son contexte et sa source). Sans effet en
         * Dialogue : Sam n'y est pas.
         */
        openCheckpoint: (activityId: number | null, trigger: LiveCheckpointTrigger) => void
        closeCheckpoint: () => void
        /** Le panneau refermé avant que Sam ait parlé, ou après un échec de son ouverture. */
        dismissCheckpointOpening: () => void
        /**
         * Le quiz de prérequis tient la place (`true`) : Sam envoyé par le tuteur attend sa fin pour
         * s'ouvrir (`samHoldReducer`).
         */
        holdSam: (held: boolean) => void
        closeNotice: () => void
        keepTab: () => void
        retryConnection?: () => void
    }
}

type RootState = {
    data?: { currentUser?: { object?: ApiStudentLiveUser | null } }
    liveMode?: {
        liveSessionId?: number | null
        isLocalPoorConnection?: boolean
        checkpointConversation?: LiveCheckpointConversation | null
    }
    activity?: { activityPaused?: boolean }
}

export function useStudentLive(options: UseStudentLiveOptions): StudentLive {
    const dispatch = useDispatch()
    const telemetry = useMemo(
        () => options.telemetry ?? createTelemetry("live-student", { role: "student" }),
        [options.telemetry]
    )
    const callbacks = useRef(options)
    callbacks.current = options

    const liveSessionId = useSelector((state: RootState) => state.liveMode?.liveSessionId ?? null)
    const user = useSelector((state: RootState) => state.data?.currentUser?.object ?? null)
    const liveSession = useMemo(
        () => user?.live_sessions?.find((session) => session?.id === liveSessionId) ?? null,
        [user?.live_sessions, liveSessionId]
    )
    const poorConnection = useSelector((state: RootState) => !!state.liveMode?.isLocalPoorConnection)
    const reduxCheckpoint = useSelector((state: RootState) => state.liveMode?.checkpointConversation ?? null)
    const tutorPaused = useSelector((state: RootState) => !!state.activity?.activityPaused)
    const me = user?.id != null ? String(user.id) : null
    const displayName: string | null = user?.display_name ?? null

    // L'écran est là : les écouteurs globaux se taisent (sons, bandeaux, navigation).
    useEffect(() => markStudentLiveScreen(), [])
    // Et il dit lui-même ses échecs réseau (la reconnexion, les envois) : les boîtes legacy se taisent.
    useEffect(() => ownNetworkFailures(), [])

    /* ── la machine de l'élève ── */
    const endTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const closeOutageRef = useRef<() => void>(() => {})
    // Sam envoyé pendant le quiz de prérequis attend sa fin (`samHoldReducer`) ; l'envoi rendu se joue
    // par `deliverSamRef`, qui lit l'état du moment.
    const samHoldRef = useRef<SamHold<LiveDataEvent>>(initialSamHold)
    const deliverSamRef = useRef<(event: LiveDataEvent) => void>(() => {})
    const holdSam = useCallback((event: SamHoldEvent<LiveDataEvent>) => {
        const step = samHoldReducer(samHoldRef.current, event)
        samHoldRef.current = step.state
        if (step.fire != null) deliverSamRef.current(step.fire)
    }, [])
    const onSignals = useCallback(
        (signals: StudentLiveSignal[]) => {
            for (const signal of signals) {
                switch (signal.type) {
                    case "transition":
                        telemetry.transition(signal.from, signal.to, signal.event)
                        break
                    case "cue":
                        callbacks.current.onCue?.(signal.cue)
                        break
                    case "close_checkpoint":
                        holdSam({ type: "dropped", reason: "pushed" })
                        closePushedOverRef.current()
                        callbacks.current.onActivityPushed?.()
                        break
                    case "restore_sound":
                        // `LKEvents` reposait le mode depuis Redux, que l'attribut vide de la reconnexion
                        // venait de passer en Concentré : écran monté, il se tait, et c'est ici.
                        setThrottledAttributes({
                            sound_mode: signal.dialogue ? "1" : "0",
                            display_name: displayName ?? "",
                        }).catch((cause: unknown) => {
                            telemetry.error("restore_sound", cause)
                        })
                        break
                    case "ended":
                        // L'activité ouverte s'arrête, comme aujourd'hui ; l'écran de fin suit. Une coupure
                        // en cours ne se refermera pas dans le live : elle s'écrit maintenant.
                        closeOutageRef.current()
                        holdSam({ type: "dropped", reason: "ended" })
                        dispatch(pauseActivity(true) as never)
                        if (endTimerRef.current != null) clearTimeout(endTimerRef.current)
                        if (signal.report === "left") callbacks.current.onEnded?.({ left: true })
                        else if (signal.report === "later") {
                            endTimerRef.current = setTimeout(
                                () => callbacks.current.onEnded?.({ left: false }),
                                END_NAVIGATION_DELAY_MS
                            )
                        }
                        break
                    case "report_left":
                        if (endTimerRef.current != null) clearTimeout(endTimerRef.current)
                        callbacks.current.onEnded?.({ left: true })
                        break
                    case "reopened":
                        if (endTimerRef.current != null) clearTimeout(endTimerRef.current)
                        dispatch(pauseActivity(false) as never)
                        callbacks.current.onReopened?.()
                        break
                }
            }
        },
        [telemetry, dispatch, holdSam, displayName]
    )
    const [state, send, read] = useSignalReducer(studentLiveReducer, () => initialStudentLiveState, onSignals)
    useEffect(
        () => () => {
            if (endTimerRef.current != null) clearTimeout(endTimerRef.current)
        },
        []
    )

    /* ── le compte, relu ── */
    const refreshAccount = useCallback(() => {
        dispatch(apiRequest(authService.getUser, {}, {}, {}, null, null, ENGINE_REQUEST_OPTIONS) as never)
    }, [dispatch])

    /** La réponse du serveur sur l'appel (lu, rejoint, main levée) : ce qu'il dit de moi. */
    const applyCallReply = useCallback(
        (data: unknown, requestedAt: number) =>
            send({ type: "CALL_REPLY", ...callReplyFor(data as never, me), requestedAt }),
        [send, me]
    )

    /** La relecture d'une reprise : le son et la main, puis le mode reposé sur la salle (`restore_sound`). */
    const readCall = useCallback(() => {
        if (liveSessionId == null) return
        const requestedAt = Date.now()
        apiCall(dispatch as never, liveServiceV2.getCallV2, { routeParams: { id: liveSessionId } })
            .then((data) => send({ type: "CALL_REPLY", ...callReplyFor(data as never, me), requestedAt, resync: true }))
            .catch((cause: unknown) => {
                telemetry.error("get_call", cause)
                send({ type: "RESYNC_UNREAD" })
            })
    }, [liveSessionId, dispatch, send, me, telemetry])

    /* ── la salle ── */
    const accountRef = useRef(user)
    accountRef.current = user
    const pushedActivityRef = useRef<Record<string, unknown> | null>(null)
    const [pushedActivity, setPushedActivity] = useState<Record<string, unknown> | null>(null)
    const [validatedNotice, setValidatedNotice] = useState<string | null>(null)
    // Les gestes du tuteur sur l'activité ouverte, que l'activité dit elle-même : archivée (sa boîte
    // « plus disponible », puis la suivante), ou rendue à la place de l'élève (sa relecture).
    const [removedActivityId, setRemovedActivityId] = useState<number | null>(null)
    const [tutorFinalizedId, setTutorFinalizedId] = useState<number | null>(null)
    const closeCheckpointOnRef = useRef<(activityIds: number[]) => void>(() => {})
    // L'activité affichée doit arriver devant l'élève : sinon une erreur le dit, avec ce que la machine croit.
    const pushWatchRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const watchPushed = useCallback(
        (activityId: number) => {
            if (pushWatchRef.current != null) clearTimeout(pushWatchRef.current)
            pushWatchRef.current = setTimeout(() => {
                const now = read()
                if (!pushedActivityOutOfSight(now, activityId)) return
                telemetry.error("pushed_not_on_screen", new Error("pushed activity not on screen"), {
                    tabs: now.tabs,
                    dialogue: now.dialogue.value,
                    samSent: now.samSent,
                    appState: AppState.currentState,
                })
            }, PUSHED_ON_SCREEN_WITHIN_MS)
        },
        [read, telemetry]
    )
    useEffect(
        () => () => {
            if (pushWatchRef.current != null) clearTimeout(pushWatchRef.current)
        },
        []
    )
    const [exitNotice, setExitNotice] = useState<ReturnType<typeof liveExitNotice> | null>(null)
    const chatReceiveRef = useRef<(event: LiveDataEvent) => void>(() => {})
    const samSentRef = useRef<(event: LiveDataEvent) => void>(() => {})
    const chatTypingRef = useRef<(senderId: string | null) => void>(() => {})
    const disconnect = useDisconnectFromLive() as { disconnectFromLive: () => Promise<void> }

    const onData = useCallback(
        (event: LiveDataEvent, senderId: string | null) => {
            const fromOther = senderId !== me
            const forMe = (id: unknown) => me != null && String(id) === me && fromOther
            switch (event.type) {
                case "finalizeLive":
                    send({ type: "CHANNEL_FINALIZE", at: Date.now() })
                    break
                case "newTodo": {
                    const { studentId, activity, forceFocus } = readNewTodo(event)
                    if (!forMe(studentId)) break
                    refreshAccount()
                    if (activity != null && forceFocus) {
                        pushedActivityRef.current = activity
                        setPushedActivity(activity)
                    }
                    const activityId = activityIdOf(activity)
                    // Lu sur le compte d'avant la relecture : une activité que l'élève avait déjà ne se dit pas.
                    const known = activityId != null && accountHasActivity(accountRef.current, activityId)
                    send({ type: "NEW_TODO", activityId, forceFocus, known })
                    if (forceFocus && activityId != null) watchPushed(activityId)
                    break
                }
                case "deleteTodo": {
                    const deleted = (Array.isArray(event.activity_ids) ? event.activity_ids : []).map(Number)
                    const { activityId: current, addedActivityId } = read()
                    // Reçu ou non, la trace le dit à côté de l'activité que la liste relue fermerait.
                    telemetry.transition("in_live", "in_live", "delete_todo", {
                        forMe: forMe(event.id),
                        deleted: deleted.join(","),
                        open: current,
                    })
                    if (!forMe(event.id)) break
                    // L'ancien écouteur ne relisait pas la liste ici (`apiRequest` sans `dispatch`).
                    refreshAccount()
                    // Un point d'avancement avec Sam sur une activité archivée se ferme, comme l'ancien.
                    closeCheckpointOnRef.current(deleted)
                    // L'activité ouverte le dit (« Cette activité n'est plus disponible »), puis passe à la
                    // suivante d'elle-même : comme l'ancien `ActivityDeletedModal`.
                    if (current != null && deleted.includes(current)) setRemovedActivityId(current)
                    // L'activité ajoutée retirée : sa barre n'a plus rien à montrer.
                    if (addedActivityId != null && deleted.includes(addedActivityId)) {
                        send({ type: "ADDED_ACTIVITY_DISMISSED" })
                    }
                    break
                }
                case "tutorFinalizedActivity": {
                    if (!forMe(event.studentId)) break
                    refreshAccount()
                    if (Number(event.activityId) !== read().activityId) break
                    const xp = Number(event.xpGained ?? 0)
                    const petals = Number(event.petals ?? 0)
                    setValidatedNotice(
                        `Activité soumise ! ${xp} XP${petals > 0 ? `, ${petals} pétale${petals > 1 ? "s" : ""}` : ""}`
                    )
                    callbacks.current.onCue?.("activity_validated")
                    // L'activité reste ouverte, rendue : elle se relit, comme l'ancien
                    // (`ActivityFinishedScreen`). L'élève passe à la suite par « Mes activités ».
                    setTutorFinalizedId(Number(event.activityId))
                    break
                }
                case "typing":
                    if (fromOther) chatTypingRef.current(senderId)
                    break
                case "newMessage":
                    if (!fromOther) break
                    chatReceiveRef.current(event)
                    callbacks.current.onCue?.("new_message")
                    break
                case "checkpointTriggerResponse":
                    // Le tuteur envoie Sam à l'élève (sa main levée), depuis ses événements ou la
                    // fenêtre de la main levée : `accepted` ; un refus ne fait rien, comme aujourd'hui.
                    if (!forMe(event.studentId) || event.accepted !== true) break
                    samSentRef.current(event)
                    break
                default:
                    break
            }
        },
        [me, send, read, refreshAccount, watchPushed, telemetry]
    )

    const onAttributesChanged = useCallback(
        (changed: Record<string, string>, participant: { isLocal: boolean }) => {
            if (!participant.isLocal) return
            const at = Date.now()
            if ("sound_mode" in changed) send({ type: "SOUND_PUSH", raw: changed.sound_mode, at })
            if ("hand_raised" in changed) send({ type: "HAND_PUSH", raw: changed.hand_raised, at })
        },
        [send]
    )

    const onExit = useCallback(
        (exit: RoomExit, serverMessage: string | null) => {
            // Sur l'écran de fin, rien ne fait sortir : l'élève sort par son bouton.
            if (read().ended != null) return
            // L'élève lit pourquoi (la séance disparue, le paiement, le jour), puis sort par « OK ».
            setExitNotice(liveExitNotice(exit, serverMessage))
        },
        [read]
    )

    const room = useLiveRoom({
        liveSessionId,
        role: "student",
        reconnectCeilingMs: STUDENT_RECONNECT_CEILING_MS,
        onData,
        onAttributesChanged,
        onSnapshot: (payload, requestedAt) => {
            const snapshot = snapshotOf(payload)
            if (snapshot != null) send({ type: "SNAPSHOT", finalized: snapshot.finalized, requestedAt })
        },
        onResync: () => {
            readCall()
            refreshAccount()
        },
        onExit,
    })
    closeOutageRef.current = room.closeOutage

    /* ── l'appel ── */
    const dialogue = state.dialogue.value
    const connectionHandle = useStudentLiveConnection({
        liveSessionId,
        userId: user?.id ?? null,
        dialogue,
        telemetry,
        onJoined: applyCallReply,
        ended: state.ended != null,
        renewToken: room.renewToken,
        currentToken: room.currentToken,
    })

    useInterval(() => {
        if (user?.email) dispatch(updateLastConnected(user) as never)
    }, LAST_CONNECTED_EVERY_MS)

    /* ── l'attente du tuteur ── */
    // Seul le principal la lève : un autre tuteur connecté (en observation, d'un autre live de la
    // salle, le titulaire remplacé) ne le remplace pas (`liveTutorCast`).
    // Le grand d'un tuteur sur deux appareils est stable : `livePeople` relit le classement d'avant.
    const peopleRef = useRef<LivePerson[]>([])
    const tutors = useMemo(() => {
        const people = livePeople(room.participants, peopleRef.current)
        peopleRef.current = people
        return liveTutorCast(liveSession, people)
    }, [liveSession, room.participants])
    const tutorPresent = tutors.principal != null
    // Mes autres appareils : relus quand l'un arrive, part, ou ouvre ou coupe sa caméra seulement.
    const otherDevicesKey = myOtherDevices(room.participants)
        .map((device) => `${device.identity}:${device.cameraOn ? 1 : 0}`)
        .join("|")
    const otherDevices = useMemo(
        () => myOtherDevices(room.participants).map(({ identity, cameraOn }) => ({ identity, cameraOn })),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [otherDevicesKey]
    )
    const inRoom = room.state.connection === "connected"
    const waiting = useTutorWaiting({
        liveSession,
        enabled: supportsNoShowRecovery(liveSession?.live_room) === true,
        inRoom,
        tutorPresent,
        skipLeaveRef: connectionHandle.skipLeaveRef,
        disconnectRoom: () => room.room?.disconnect(true) ?? Promise.resolve(),
        telemetry,
    })
    useEffect(() => {
        if (waiting.attention > 0) callbacks.current.onCue?.("attention")
    }, [waiting.attention])

    /* ── le chat ── */
    const [chatOpen, setChatOpen] = useState(false)
    // Rien de choisi : le fil du tuteur de la séance, dès que la séance le dit.
    const [chosenChannel, setChatChannel] = useState<LiveChatChannel | null>(null)
    const chatChannel = resolveChatChannel(liveSession, chosenChannel)
    const chat = useLiveChat({
        session: liveSession,
        user,
        open: chatOpen,
        channel: chatChannel,
        send: room.send,
        telemetry,
        voiceOutbox: options.voiceOutbox,
    })
    chatReceiveRef.current = chat.receive
    chatTypingRef.current = chat.receiveTyping
    const liveSessionRef = useRef(liveSession)
    liveSessionRef.current = liveSession
    const setChatOpenOnUnread = useCallback((open: boolean) => {
        // Le chat s'ouvre sur le fil qui attend l'élève : relu, c'est lui qui fait retomber la pastille.
        if (open) {
            setChatChannel((current) =>
                chatChannelToOpen(liveSessionRef.current, resolveChatChannel(liveSessionRef.current, current))
            )
        }
        setChatOpen(open)
    }, [])
    // Une notification de message de cette séance, touchée (natif) : le chat s'ouvre sur son fil.
    useLiveNotificationTap(liveSession, (channel) => {
        setChatChannel(channel)
        setChatOpen(true)
    })

    /* ── la sortie ── */
    const leave = useLiveLeave(liveSessionId, telemetry, {
        beforeLeave: () => {
            room.closeOutage()
            send({ type: "LEAVE_COMMITTED" })
        },
        onOut: (exit) => {
            send({ type: "LEAVE_OUT" })
            callbacks.current.onOut?.(exit)
        },
    })
    // L'élève quitte : un Sam qui attendait la fin du quiz ne s'ouvrira pas.
    useEffect(() => {
        if (leave.leaving) holdSam({ type: "dropped", reason: "left" })
    }, [leave.leaving, holdSam])

    /* ── les checkpoints ── */
    // Une conversation rangée par une séance d'avant (`liveMode` est persisté) ne se rouvre pas.
    const staleCheckpointRef = useRef<unknown>(reduxCheckpoint?.conversation?.id ?? null)
    useEffect(() => {
        if (staleCheckpointRef.current != null) {
            telemetry.transition("restored", "dropped", "stale_checkpoint")
            dispatch(clearCheckpointConversation() as never)
        }
        // Au montage seulement.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
    // Comme `SamCheckpointOverlay` : la conversation de Sam en live est celle des salles qui ont le drapeau.
    const checkpointsEnabled = hasFeature(liveSession?.live_room, "checkpoint-rh") === true
    const [openingState, dispatchOpening] = useReducer(checkpointOpeningReducer, initialCheckpointOpening)
    const checkpointOpening = openingState.phase
    // La création lit l'ouverture à son retour : la référence suit chaque événement, sans attendre le rendu.
    const openingRef = useRef<CheckpointOpeningState>(openingState)
    const sendOpening = useCallback((event: CheckpointOpeningEvent) => {
        openingRef.current = checkpointOpeningReducer(openingRef.current, event)
        dispatchOpening(event)
    }, [])
    const checkpoint = liveCheckpointToShow({
        enabled: checkpointsEnabled,
        conversation: reduxCheckpoint,
        staleId: staleCheckpointRef.current,
        dialogue,
        opening: checkpointOpening,
    })

    /** La conversation se dit interrompue au serveur, si elle court encore. */
    const finishCheckpoint = useCallback(
        (conversation: LiveCheckpointConversation["conversation"], reason: string) => {
            if (conversation?.id == null || conversation.status !== "ongoing") return
            apiCall(dispatch as never, liveService.finishConversation, {
                routeParams: { conversation_id: conversation.id },
                data: { interruption_reason: reason, ended_at: new Date().toISOString() },
            }).catch((cause: unknown) => {
                telemetry.error("checkpoint_interrupt", cause, { reason })
            })
        },
        [dispatch, telemetry]
    )
    /** La conversation se dit interrompue au serveur (si elle court encore), puis quitte le magasin. */
    const interruptCheckpoint = useCallback(
        (conversation: LiveCheckpointConversation["conversation"], reason: string) => {
            finishCheckpoint(conversation, reason)
            dispatch(clearCheckpointConversation() as never)
        },
        [dispatch, finishCheckpoint]
    )
    // La conversation que la dernière création a rangée, telle que le magasin la tient depuis (les
    // réponses de Sam la mettent à jour). Une création abandonnée qui revient après elle l'écrase dans le
    // magasin : on la remet.
    const keptCheckpointRef = useRef<LiveCheckpointConversation | null>(null)
    useEffect(() => {
        const kept = keptCheckpointRef.current
        if (reduxCheckpoint == null) keptCheckpointRef.current = null
        else if (kept != null && reduxCheckpoint.conversation?.id === kept.conversation?.id) {
            keptCheckpointRef.current = reduxCheckpoint
        }
    }, [reduxCheckpoint])

    closeCheckpointOnRef.current = (activityIds) => {
        const activityId = checkpoint?.conversation?.activity_id
        if (activityId != null && activityIds.includes(Number(activityId))) closePushedOverRef.current()
    }

    const closePushedOverRef = useRef<() => void>(() => {})
    closePushedOverRef.current = () => {
        // L'activité poussée par le tuteur ferme la conversation en cours, comme aujourd'hui.
        if (checkpoint?.conversation?.id != null) interruptCheckpoint(checkpoint.conversation, "tutor_interruption")
    }

    // Le tuteur met l'élève en Dialogue : la conversation en cours se ferme, interrompue par lui (le
    // serveur garde les réponses déjà données), et une ouverture en route est abandonnée.
    const reduxCheckpointRef = useRef(reduxCheckpoint)
    reduxCheckpointRef.current = reduxCheckpoint
    const wasDialogueRef = useRef(dialogue)
    useEffect(() => {
        const entered = dialogue && !wasDialogueRef.current
        wasDialogueRef.current = dialogue
        if (!entered) return
        sendOpening({ type: "dismissed" })
        holdSam({ type: "dropped", reason: "dialogue" })
        const held = liveCheckpointToShow({
            enabled: checkpointsEnabled,
            conversation: reduxCheckpointRef.current,
            staleId: staleCheckpointRef.current,
            dialogue: false,
            opening: "idle",
        })
        if (held?.conversation?.id == null) return
        telemetry.transition("checkpoint", "closed", "dialogue")
        interruptCheckpoint(held.conversation, "tutor_interruption")
    }, [dialogue, checkpointsEnabled, interruptCheckpoint, telemetry, sendOpening, holdSam])

    const openCheckpoint = useCallback(
        (activityId: number | null, trigger: LiveCheckpointTrigger) => {
            if (liveSessionId == null || user?.id == null || openingRef.current.phase === "opening") return
            // Sam n'est pas là en Dialogue : l'écran dit pourquoi.
            if (read().dialogue.value) return
            sendOpening({ type: "requested" })
            const request = openingRef.current.request
            // La même création que celle de la main levée acceptée par le tuteur : le réducteur la range
            // (`checkpointConversation`), l'écran la pose.
            apiCall<LiveCheckpointConversation | null>(dispatch as never, liveService.createConversation, {
                data: checkpointCreationBody({ studentId: user.id, liveSessionId, activityId, trigger }),
            })
                .then((created) => {
                    const superseded = request !== openingRef.current.request
                    if (superseded) {
                        // Refermé puis retouché : une demande plus récente est en route ou déjà rangée.
                        // Celle-ci se dit interrompue, et le magasin rend la conversation de la dernière.
                        finishCheckpoint(created?.conversation, "student_dismiss")
                        dispatch(
                            (keptCheckpointRef.current != null
                                ? setCheckpointConversation(keptCheckpointRef.current)
                                : clearCheckpointConversation()) as never
                        )
                        return
                    }
                    if (creationAbandoned(openingRef.current, request)) {
                        // Refermé avant que Sam ait parlé : elle ne se montre pas, et se dit interrompue.
                        interruptCheckpoint(created?.conversation, "student_dismiss")
                        // Sam envoyé par le tuteur, refermé avant d'avoir parlé : il rend la main au mode.
                        send({ type: "SAM_RELEASED" })
                    } else {
                        keptCheckpointRef.current = created
                    }
                    sendOpening({ type: "settled", request })
                })
                .catch((cause: unknown) => {
                    telemetry.error("checkpoint_open", cause)
                    // Une demande remplacée : la dernière dira son issue.
                    if (request !== openingRef.current.request) return
                    sendOpening({ type: "failed", request })
                    // Sam envoyé par le tuteur ne s'est pas ouvert : il rend la main au mode.
                    send({ type: "SAM_RELEASED" })
                })
        },
        [liveSessionId, user?.id, read, dispatch, telemetry, finishCheckpoint, interruptCheckpoint, sendOpening, send]
    )

    /* ── les gestes ── */
    const setAttributes = useThrottledSetAttributes()
    const { localParticipant, isMicrophoneEnabled, isCameraEnabled, microphoneTrack } = useLocalParticipant()

    const toggleHand = useCallback(() => {
        if (liveSessionId == null || read().handPending) return
        const raised = !read().handRaised.value
        const requestedAt = Date.now()
        send({ type: "HAND_TOGGLED", at: requestedAt })
        setAttributes({
            raise_hand_source: "",
            display_name: user?.display_name ?? "",
            // Comme aujourd'hui : sans activité ouverte, le tuteur n'a pas de checkpoint à proposer.
            skip_checkpoint: read().activityId == null ? "1" : "0",
        }).catch((cause: unknown) => {
            telemetry.error("hand_attributes", cause)
        })
        apiCall(dispatch as never, liveServiceV2.raiseHandV2, { routeParams: { id: liveSessionId }, data: { raised } })
            .then((data) => applyCallReply(data, requestedAt))
            .catch((cause: unknown) => {
                telemetry.error("raise_hand", cause)
                send({ type: "HAND_FAILED" })
            })
    }, [liveSessionId, read, send, setAttributes, user?.display_name, dispatch, applyCallReply, telemetry])

    /*
     * Sam envoyé par le tuteur (Wajih, 20/10) : il passe devant, la phrase le dit, et sa conversation
     * s'ouvre, comme l'ancien écouteur (`useLiveKitEventListener`), la main baissée. Une conversation
     * déjà ouverte est gardée : on n'en empile pas une seconde, et le geste se trace. Pendant le quiz de
     * prérequis, il attend sa fin (`samHoldReducer`).
     */
    const checkpointOpen = checkpoint != null
    samSentRef.current = (event: LiveDataEvent) => {
        // Avant tout le reste, en Dialogue, sans Sam ou pendant le quiz : le tuteur a répondu à la main.
        const { lowerHand } = samSentOutcome({
            checkpointsEnabled,
            dialogue: read().dialogue.value,
            conversationOpen: checkpointOpen,
            handRaised: read().handRaised.value,
        })
        if (lowerHand) toggleHand()
        if (samHoldRef.current.held) telemetry.transition("in_live", "in_live", "sam_sent_held_by_quiz")
        holdSam({ type: "sent", event })
    }
    deliverSamRef.current = (event: LiveDataEvent) => {
        const { outcome } = samSentOutcome({
            checkpointsEnabled,
            dialogue: read().dialogue.value,
            conversationOpen: checkpointOpen,
            handRaised: read().handRaised.value,
        })
        if (outcome === "without_checkpoints" || outcome === "in_dialogue") {
            telemetry.transition("in_live", "in_live", `sam_sent_${outcome}`)
            return
        }
        send({ type: "SAM_SENT", kept: outcome === "keep" })
        if (outcome === "open") {
            openCheckpoint(read().activityId, {
                context: typeof event.trigger_context === "string" ? event.trigger_context : "raise_hand",
                source: typeof event.trigger_source === "string" ? event.trigger_source : "tutor_events_panel",
            })
        }
    }
    // Sa conversation refermée, Sam rend la main au mode.
    const hadCheckpointRef = useRef(false)
    useEffect(() => {
        if (checkpointOpen) {
            hadCheckpointRef.current = true
            return
        }
        if (!hadCheckpointRef.current) return
        hadCheckpointRef.current = false
        send({ type: "SAM_RELEASED" })
    }, [checkpointOpen, send])

    // La porte du micro tenu s'ouvre à la demande réussie (elle est créée après le refus, qu'elle lit).
    const micGateOpenedRef = useRef<() => void>(() => undefined)
    const {
        retry: retryMic,
        request: requestMic,
        dismiss: dismissMicRefusal,
        ...micRefusal
    } = useMicRefusal({
        localParticipant,
        dialogue,
        micOn: isMicrophoneEnabled,
        permissionDenied: options.micPermissionDenied === true,
        onOpened: () => micGateOpenedRef.current(),
        telemetry,
    })
    const {
        dismiss: dismissMicAsk,
        opened: micGateOpened,
        ...micGate
    } = useMicGate({
        dialogue,
        dialogueKnown: state.dialogue.known,
        micOn: isMicrophoneEnabled,
        connected: inRoom,
        refused: micRefusal.refused,
        telemetry,
    })
    micGateOpenedRef.current = micGateOpened
    const activateMic = useCallback(() => {
        telemetry.transition("mic_ask", "mic_ask", "activate")
        requestMic()
    }, [telemetry, requestMic])
    // Le micro ouvert qui ne capte rien (coupé par l'ordinateur) : écouté en Dialogue seulement.
    const micSilent = useMicSilence({
        // La piste de LiveKit elle-même : la mesure lit son filtre et suit ses relances.
        track: (microphoneTrack?.track as LocalAudioTrack | undefined) ?? null,
        active: dialogue && isMicrophoneEnabled,
        telemetry,
    })
    // Le micro tombé (la piste perdue, que LiveKit n'a pas pu relancer) : la salle le rouvre, l'écran le dit.
    const { dropped: micDropped, cut: cutMic } = useMicDrop({
        localParticipant,
        dialogue,
        ready: inRoom && !micGate.held && !micRefusal.refused,
        micOn: isMicrophoneEnabled,
        telemetry,
    })
    const toggleMic = useCallback(() => {
        if (!read().dialogue.value) {
            send({ type: "MIC_TOUCHED" })
            return
        }
        if (!isMicrophoneEnabled) {
            // Dans le geste : un refus a sa trace, une par épisode (`mic_refused`), les autres échecs la leur.
            requestMic()
            return
        }
        const request = localParticipant.setMicrophoneEnabled(false)
        cutMic(request)
        request.catch((cause: unknown) => {
            telemetry.error("toggle_mic", cause)
        })
    }, [read, send, localParticipant, isMicrophoneEnabled, telemetry, requestMic, cutMic])

    const { report: reportCameraFailure, blocker: cameraBlocker } = useCameraRefusal({
        localParticipant,
        cameraOn: isCameraEnabled,
        telemetry,
    })
    const toggleCamera = useCallback(() => {
        localParticipant.setCameraEnabled(!isCameraEnabled).catch((cause: unknown) => {
            if (!reportCameraFailure(cause)) telemetry.error("toggle_camera", cause)
        })
    }, [localParticipant, isCameraEnabled, telemetry, reportCameraFailure])

    /* ── ce que la scène dit de la connexion ── */
    const connection = connectionHandle.unreachable
        ? "unreachable"
        : room.state.connection === "connected" || room.state.connection === "evicted"
          ? null
          : room.state.everConnected
            ? "reconnecting"
            : "connecting"
    const tabsNotice = room.tabs.showKeepTabModal ? "several_tabs" : null

    // Des gestes stables d'un rendu à l'autre : un bandeau relance son minuteur quand son `onDismiss`
    // change, et le chrono de l'activité ne doit pas redessiner la page.
    const disconnectRef = useRef(disconnect)
    disconnectRef.current = disconnect
    const simpleActions = useMemo(
        () => ({
            changeView: (view: StudentLiveView) => send({ type: "VIEW_CHANGED", view }),
            changeLayout: (tabs: boolean) => send({ type: "LAYOUT_CHANGED", tabs }),
            dismissTutorTalking: () => send({ type: "TUTOR_TALKING_DISMISSED" }),
            dismissMicHint: () => send({ type: "MIC_HINT_DISMISSED" }),
            dismissSamBubble: () => send({ type: "SAM_BUBBLE_DISMISSED" }),
            dismissNewActivity: () => send({ type: "NEW_ACTIVITY_DISMISSED" }),
            dismissAddedActivity: () => send({ type: "ADDED_ACTIVITY_DISMISSED" }),
            dismissSamSentNotice: () => send({ type: "SAM_SENT_NOTICE_DISMISSED" }),
            dismissValidated: () => setValidatedNotice(null),
            selectActivity: (activityId: number | null, chosen?: boolean) => {
                if (activityId == null || activityId !== activityIdOf(pushedActivityRef.current)) {
                    pushedActivityRef.current = null
                    setPushedActivity(null)
                }
                send({ type: "ACTIVITY_SELECTED", activityId, chosen })
            },
            reportActivityOnScreen: (onScreen: boolean) => send({ type: "ACTIVITY_ON_SCREEN", onScreen }),
            closeCheckpoint: () => dispatch(clearCheckpointConversation() as never),
            dismissCheckpointOpening: () => sendOpening({ type: "dismissed" }),
            holdSam: (held: boolean) => holdSam({ type: "held", held }),
            closeNotice: () => {
                setExitNotice(null)
                closeOutageRef.current()
                disconnectRef.current.disconnectFromLive().catch((cause: unknown) => {
                    telemetry.error("exit_live", cause)
                })
            },
        }),
        [send, dispatch, telemetry, sendOpening, holdSam]
    )

    return {
        liveSessionId,
        user,
        liveSession,
        room,
        state,
        dialogue,
        micOn: isMicrophoneEnabled,
        micRefusal,
        micGate,
        micSilent,
        micDropped,
        cameraOn: isCameraEnabled,
        cameraBlocker,
        poorConnection,
        connection,
        notice: exitNotice?.notice ?? tabsNotice,
        noticeMessage: exitNotice?.message ?? null,
        validatedNotice,
        removedActivityId,
        tutorFinalizedId,
        pushedActivity,
        checkpoint,
        checkpointsEnabled,
        checkpointOpening,
        waiting,
        tutors,
        otherDevices,
        chat: {
            ...chat,
            open: chatOpen,
            channel: chatChannel,
            setOpen: setChatOpenOnUnread,
            setChannel: setChatChannel,
        },
        leave,
        activityPaused: tutorPaused,
        send: room.send,
        setAttributes,
        telemetry,
        actions: {
            ...simpleActions,
            toggleHand,
            toggleMic,
            retryMic,
            dismissMicRefusal,
            activateMic,
            dismissMicAsk,
            toggleCamera,
            openCheckpoint,
            keepTab: room.tabs.keepThisTab,
            retryConnection: connectionHandle.unreachable ? connectionHandle.retry : room.webReconnect.onRetry,
        },
    }
}

function activityIdOf(activity: Record<string, unknown> | null | undefined): number | null {
    const id = activity?.id
    return typeof id === "number" ? id : typeof id === "string" && id !== "" ? Number(id) : null
}
